import { BusinessError, SystemUserAssetType } from '@marketing-milk/interfaces'
import { Button, LinearProgress } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import Redux from 'app/interface/redux'
import Basic from 'app/redux/actions/pages/business/basic'
import Store from 'app/redux/store'
import Connect from 'app/redux/connect'
import { businessManagerService } from 'app/service/business-manager'
import {
  FBAssetsRefreshStatus,
  FBAssignAssetResult,
} from 'app/service/business-manager/BusinessManager.types'
import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { FBObjectTypeString } from './facebook-object-selector'
import FbAssignableField, { ConnectedPerson } from './FbAssignableField'
import Popover from 'components/reusable/popover'
import RefreshAssetsDialog from './RefreshAssetsDialog'
import SystemUserAlignDialog from './SystemUserAlignDialog'

// Which FBObjectTypeString(s) each live BusinessError flag maps to, so the panel's per-field health
// chips reflect the existing async check-fb-access results without a new health-check mechanism.
// lead_access_error is deliberately excluded here — it's surfaced as a warning (see
// pageLeadWarningMessage below), not an error, since the Page connection itself still works fine
// without lead access; only lead syncing is affected.
const ERROR_TO_FIELDS: Partial<Record<BusinessError, FBObjectTypeString[]>> = {
  [BusinessError.ad_account_access_error]: ['ad_account'],
  [BusinessError.page_access_error]: ['page'],
  [BusinessError.offline_upload_error]: ['pixel', 'offline'],
}

// lead_access_error is non-fatal — the Page connection itself is fine, only lead syncing is affected —
// so it's shown as a warning on the Page field rather than folded into fieldHasError's red "Needs
// attention" state alongside a real page_access_error.
// How often the panel re-reads refresh progress while a Business Manager re-sync is in flight (DRI-246)
const REFRESH_POLL_MS = 5000

const pageLeadWarningMessage =
  'This page did not grant Lead Access. Leads will not sync until this is resolved.'

// Maps the backend's SystemUserAssetType (used by fb_business_info) to the selector's
// FBObjectTypeString — the two evolved independently, same as FbAssignableField's FIELD_MAP.
const ASSET_TYPE_TO_FIELD: Record<SystemUserAssetType, FBObjectTypeString> = {
  adaccount: 'ad_account',
  page: 'page',
  dataset: 'pixel',
  offline: 'offline',
  instagram: 'ig',
}

interface FacebookProps extends Redux {
  new: boolean
}

const Facebook = (props: FacebookProps) => {
  const { main } = props.pages.business
  // DRI-408: only a saved business has a system user to read its ad account's datasets/IG accounts with
  const businessID = props.new ? undefined : props.pages.business.id || undefined
  const fbConnected = props.core.session.user!.fbConnected ?? false

  const [accountID, setAccountID] = useState<string | undefined>(main.fb_account_id)
  const [pageID, setPageID] = useState<string | undefined>(main.fb_page_id ?? undefined)
  const [datasetID, setDatasetID] = useState<string | undefined>(main.fb_dataset_id ?? undefined)
  const [offlineID, setOfflineID] = useState<string | undefined>(
    main.offline_event_set_id ?? undefined
  )
  const [instagramID, setInstagramID] = useState<string | undefined>(main.instagram_id ?? undefined)
  // "Refresh Available Assets" is a server-side Business Manager re-sync that takes minutes (DRI-246), so
  // its progress lives on the server: read on mount (a refresh may already be running, started earlier or
  // by someone else on the same BMs) and polled while one is in flight.
  const [refreshStatus, setRefreshStatus] = useState<FBAssetsRefreshStatus | null>(null)
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false)
  const [startingRefresh, setStartingRefresh] = useState(false)
  const refreshingAssets = !!refreshStatus && refreshStatus.status !== 'idle'
  const wasRefreshing = useRef(false)
  // Bumped after clearFacebookAccessCache resolves, to re-trigger the Ad Account/Page fields' own
  // default (empty-search) fetch against the now-cleared server cache — see FacebookObjectSelector's
  // SEARCHED_TYPES comment.
  const [refreshToken, setRefreshToken] = useState(0)
  // How many of the Ad Account/Page fields' post-refresh fetches are still outstanding, plus the
  // resolver for the promise handleRefreshFacebookAccess is awaiting — a ref since it's read and written
  // from callbacks across renders (handleAssetsRefreshed below) rather than something that itself needs
  // to trigger a re-render.
  const pendingAssetFetches = useRef<{ remaining: number; resolve: () => void } | null>(null)

  const handleAssetsRefreshed = () => {
    if (!pendingAssetFetches.current) return
    pendingAssetFetches.current.remaining -= 1
    if (pendingAssetFetches.current.remaining <= 0) {
      pendingAssetFetches.current.resolve()
      pendingAssetFetches.current = null
    }
  }

  // The fields (re)assigned or cleared since the last save: each shows an Updated tag, and the panel a
  // "Changes pending" notice, since per-field assign-and-verify already ran against Facebook but the business
  // record itself isn't persisted until Save. Kept in redux, not here, so it survives the panel remounting when
  // its tab is left and reopened; a successful save clears it.
  const pendingFields = (props.pages.business.fb_pending_fields ?? []) as FBObjectTypeString[]
  const hasPendingChanges = pendingFields.length > 0
  const markPending = (...fields: FBObjectTypeString[]) => Basic.markFacebookFieldsPending(fields)

  // Set when a currently-assigned Dataset/Offline Dataset/Instagram Account turns out to no longer be
  // connected to the ad account after the Ad Account itself changes — none of the three are otherwise
  // re-verified by an Ad Account change alone, since only the field actually being assigned gets checked.
  const [adAccountDependentWarnings, setAdAccountDependentWarnings] = useState<
    Partial<Record<'pixel' | 'offline' | 'ig', string>>
  >({})

  // Fields confirmed healthy by a real live grant this session, overriding a stale check-fb-access flag
  // for that field until the next Save-triggered check re-evaluates for real. Unlike the reverted
  // "just assume a successful in-session assign means it's fine" approach, this is only set when we have
  // an actual live confirmation — a successful per-field assign (assignSingleFacebookAsset) throws on
  // grant failure, so getting here means it didn't.
  const [locallyConfirmedFields, setLocallyConfirmedFields] = useState<
    Partial<Record<FBObjectTypeString, true>>
  >({})

  // BM display info per field (id/name/picture) — seeded from fb_business_info on the initial business
  // load (resolved server-side off each asset's system user), then kept in sync locally as per-field
  // assigns happen this session. Not part of BusinessConfig, so it's never sent back on save.
  const [bmInfoByField, setBmInfoByField] = useState<
    Partial<Record<FBObjectTypeString, { id?: string; name?: string; pictureUri?: string }>>
  >(() => {
    const initial: Partial<
      Record<FBObjectTypeString, { id?: string; name?: string; pictureUri?: string }>
    > = {}
    const info = props.pages.business.fb_business_info
    if (info) {
      for (const [assetType, bmInfo] of Object.entries(info) as [
        SystemUserAssetType,
        (typeof info)[SystemUserAssetType]
      ][]) {
        if (bmInfo) {
          initial[ASSET_TYPE_TO_FIELD[assetType]] = {
            id: bmInfo.id,
            name: bmInfo.name,
            pictureUri: bmInfo.pictureUri,
          }
        }
      }
    }
    return initial
  })

  // DRI-442: the Business Manager that owns the Ad Account / Page. bmInfoByField holds the one whose system
  // user connects them (fb_business_info), which is often an agency BM, so the field shows both. Dataset,
  // Offline Dataset and Instagram have no system user, so their bmInfoByField entry is already the owner.
  const [ownerInfoByField, setOwnerInfoByField] = useState<
    Partial<Record<'ad_account' | 'page', { id?: string; name?: string; pictureUri?: string }>>
  >(() => {
    const owners = props.pages.business.fb_asset_owner_info
    return { ad_account: owners?.adaccount, page: owners?.page }
  })

  // DRI-442: ad sets and ads are created with the Ad Account's system user, which Facebook requires to have
  // Advertise access on the Page, so the two fields belong on one system user. Worked out from the panel's
  // own (possibly unsaved) state rather than the stored fb_system_user_mismatch flag, so it updates the
  // moment either field changes.
  const [alignDialogOpen, setAlignDialogOpen] = useState(false)

  // DRI-253: the business is on one person, an override on top of the system users, when an authorizer is set.
  // Only offered when no system user of ours reaches both assets; picking a system user again replaces it.
  const sessionUser = props.core.session.user!
  const [personInfo, setPersonInfo] = useState(props.pages.business.fb_person_info)
  const onPerson = !!main.fb_ad_authorizer_id || !!main.fb_page_authorizer_id
  const connectedBy: ConnectedPerson | undefined = onPerson
    ? {
        name: personInfo?.name ?? 'a personal login',
        tokenValid: personInfo?.token_valid ?? true,
        pictureUri: personInfo?.picture_uri,
      }
    : undefined

  // The panel remounts whenever its tab is left and reopened, reseeding from the business in redux, so keep
  // what it shows there as it changes, not just the asset ids.
  useEffect(() => {
    const fbBusinessInfo: NonNullable<typeof props.pages.business.fb_business_info> = {}
    for (const [assetType, field] of Object.entries(ASSET_TYPE_TO_FIELD) as [
      SystemUserAssetType,
      FBObjectTypeString
    ][]) {
      const bm = bmInfoByField[field]
      if (bm?.id)
        fbBusinessInfo[assetType] = { id: bm.id, name: bm.name, pictureUri: bm.pictureUri }
    }
    const owner = (bm?: { id?: string; name?: string; pictureUri?: string }) =>
      bm?.id ? { id: bm.id, name: bm.name, pictureUri: bm.pictureUri } : undefined
    Basic.setFacebookDisplayInfo({
      fb_business_info: fbBusinessInfo,
      fb_asset_owner_info: {
        adaccount: owner(ownerInfoByField.ad_account),
        page: owner(ownerInfoByField.page),
      },
      fb_person_info: personInfo,
    })
  }, [bmInfoByField, ownerInfoByField, personInfo])
  // an asset whose assign failed, handed to the Fix dialog so the viewer can connect it with their own login
  const [alignCandidate, setAlignCandidate] = useState<{
    field: 'ad_account' | 'page'
    id: string
    name: string
  }>()
  const adSystemUserId = main.adaccount_system_user_id ?? undefined
  const pageSystemUserId = main.page_system_user_id ?? undefined
  // never for a business on a person (DRI-253): their one login covers both, whatever system users sit underneath
  const systemUsersSplit =
    !main.fb_ad_authorizer_id &&
    !main.fb_page_authorizer_id &&
    !!accountID &&
    !!pageID &&
    !!adSystemUserId &&
    !!pageSystemUserId &&
    adSystemUserId !== pageSystemUserId
  // A split alone doesn't block publish: the Ad Account's system user may also hold Advertise on the Page. Ask
  // Facebook the same question the publish guard and the stored flag ask, so the panel never claims a block
  // that isn't there. Null while unknown (loading, or Facebook couldn't be asked).
  const [adCanAdvertiseOnPage, setAdCanAdvertiseOnPage] = useState<boolean | null>(null)
  useEffect(() => {
    setAdCanAdvertiseOnPage(null)
    if (!systemUsersSplit) return
    let cancelled = false
    businessManagerService
      .getFBSystemUserOptions({
        adAccountId: accountID,
        pageId: pageID,
        adSystemUserId,
        pageSystemUserId,
      })
      .then(options => {
        if (cancelled) return
        // left out of the Page's options when it can't reach the Page at all
        const option = options.page.find(o => o.systemUserId === adSystemUserId)
        setAdCanAdvertiseOnPage(
          !option ? false : option.tasks === null ? null : option.tasks.includes('ADVERTISE')
        )
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [systemUsersSplit, accountID, pageID, adSystemUserId, pageSystemUserId])

  const bmName = (field: 'ad_account' | 'page') => bmInfoByField[field]?.name ?? 'an unknown one'
  // no system user ids here: the Business Manager names are what an admin can act on
  const splitConsequence =
    adCanAdvertiseOnPage === true
      ? "Ads can still publish, since the Ad Account's system user has Advertise access on the Page, but one system user for both is recommended. Click Fix."
      : adCanAdvertiseOnPage === false
      ? 'Ads can\'t publish ("Insufficient Page Permission to Run Ads") until both use one system user. Click Fix.'
      : 'Ads may not publish ("Insufficient Page Permission to Run Ads") until both use one system user. Click Fix.'
  const sameBM =
    !!bmInfoByField.ad_account?.id && bmInfoByField.ad_account?.id === bmInfoByField.page?.id
  const adSystemUserWarning = systemUsersSplit
    ? `${
        sameBM
          ? 'This Ad Account uses a different system user than the Page.'
          : `This Ad Account's system user is in ${bmName(
              'ad_account'
            )}, but the Page's is in ${bmName('page')}.`
      } ${splitConsequence}`
    : undefined
  const pageSystemUserWarning = systemUsersSplit
    ? `${
        sameBM
          ? 'This Page uses a different system user than the Ad Account.'
          : `This Page's system user is in ${bmName('page')}, but the Ad Account's is in ${bmName(
              'ad_account'
            )}.`
      } ${splitConsequence}`
    : undefined

  const errors: BusinessError[] = props.pages.business.errors ?? []
  const fieldHasError = (field: FBObjectTypeString) =>
    !locallyConfirmedFields[field] && errors.some(error => ERROR_TO_FIELDS[error]?.includes(field))

  // Dataset/Offline Dataset/Instagram are only re-verified against the ad account when that field
  // itself is (re)assigned — an Ad Account change alone doesn't touch them, so any of the three can
  // silently go stale. Re-check all against the new ad account whenever it changes, purely as read-only
  // checks (no re-grant): dataset connectivity via the dedicated fb-dataset-connection-check endpoint,
  // Instagram by re-listing the ad account's Instagram accounts and checking membership (same call the
  // dropdown itself uses). A newly picked ad account isn't saved yet, so the backend reads it with the
  // viewer's own token; only the business's saved ad account is read with its system user (DRI-408).
  const recheckAdAccountDependentFields = async (newAdAccountId: string) => {
    if (datasetID) {
      try {
        const { connected } = await businessManagerService.checkDatasetConnection(
          datasetID,
          newAdAccountId,
          businessID
        )
        const message = connected
          ? undefined
          : 'This Dataset is no longer connected to the newly selected Ad Account. Check the connection in Events Manager, or update which Dataset is selected.'
        if (message) toast.warn(message)
        setAdAccountDependentWarnings(prev => ({ ...prev, pixel: message }))
      } catch (e) {
        console.error(e)
      }
    }

    if (offlineID) {
      try {
        const { connected } = await businessManagerService.checkDatasetConnection(
          offlineID,
          newAdAccountId,
          businessID
        )
        const message = connected
          ? undefined
          : 'This Offline Dataset is no longer connected to the newly selected Ad Account. Check the connection in Events Manager, or update which Dataset is selected.'
        if (message) toast.warn(message)
        setAdAccountDependentWarnings(prev => ({ ...prev, offline: message }))
      } catch (e) {
        console.error(e)
      }
    }

    if (instagramID) {
      try {
        const igAccounts = await businessManagerService.getFBIGAccounts(newAdAccountId, businessID)
        const match = igAccounts.find(ig => ig.id === instagramID)
        const message = match
          ? undefined
          : 'This Instagram account is no longer connected to the newly selected Ad Account.'
        if (message) toast.warn(message)
        setAdAccountDependentWarnings(prev => ({ ...prev, ig: message }))
        // The field also falls back to the page-load connected flag and picture, which were resolved
        // against the previous ad account. Update both from this check, or a passing check still shows
        // "not connected" with no picture until the business is saved and reloaded (DRI-286).
        Basic.update('instagram_connected', !!match)
        if (match?.picture?.data.url) Basic.update('instagram_picture_uri', match.picture.data.url)
      } catch (e) {
        console.error(e)
      }
    }
  }

  // DRI-253: connect both fields through the viewer's own Facebook login, checked by the Fix dialog first. The
  // system user ids stay as the target; the save resolves the viewer's token for both authorizer columns.
  const connectWithPerson = (candidate = alignCandidate) => {
    if (candidate?.field === 'ad_account') {
      setAccountID(candidate.id)
      Basic.update('fb_account_id', candidate.id)
      Basic.update('fb_account_name', candidate.name)
      Basic.update('adaccount_system_user_id', null)
      setOwnerInfoByField(prev => ({ ...prev, ad_account: undefined }))
      recheckAdAccountDependentFields(candidate.id)
    } else if (candidate?.field === 'page') {
      setPageID(candidate.id)
      Basic.update('fb_page_id', candidate.id)
      Basic.update('fb_page_name', candidate.name)
      Basic.update('page_system_user_id', null)
      setOwnerInfoByField(prev => ({ ...prev, page: undefined }))
    }
    Basic.update('fb_ad_authorizer_id', sessionUser.id)
    Basic.update('fb_page_authorizer_id', sessionUser.id)
    setPersonInfo({
      id: sessionUser.id,
      name: `${sessionUser.firstName} ${sessionUser.lastName}`.trim(),
      token_valid: true,
    })
    setLocallyConfirmedFields(prev => ({ ...prev, ad_account: true, page: true }))
    markPending('ad_account', 'page')
  }

  // With no Page set yet there's nothing for the Fix dialog to align, and the Ad Account is the first field picked
  // on a new business: check the viewer's own access to the Ad Account alone and connect them if it's enough.
  // The Page, once picked, is checked against the same person after Save.
  const connectAdAccountWithPerson = async (id: string, name: string) => {
    try {
      const access = await businessManagerService.getFBPersonAccess({ adAccountId: id })
      if (access.ready) {
        connectWithPerson({ field: 'ad_account', id, name })
        toast.success('Connected with your Facebook login. Click Save to keep this change.')
        return
      }
      const missing = ['ADVERTISE', 'ANALYZE']
        .filter(t => !access.adAccount?.tasks?.includes(t))
        .map(t => (t === 'ADVERTISE' ? 'Manage campaigns (ads)' : 'View performance'))
      toast.error(
        access.adAccount?.tasks === null
          ? "Couldn't check your access on Facebook. Try again."
          : `Your Facebook login is missing ${missing.join(' and ')} on this Ad Account.`
      )
    } catch {
      toast.error("Couldn't check your access on Facebook. Try again.")
    }
  }

  // `quiet`: the Fix dialog moves both fields back to back and shows its own result, so no per-field toasts
  const handleAssigned = (
    field: FBObjectTypeString,
    result: FBAssignAssetResult,
    assetId: string,
    assetName: string,
    quiet = false
  ) => {
    setBmInfoByField(prev => ({
      ...prev,
      [field]: {
        id: result.fbBusinessId,
        name: result.fbBusinessName,
        pictureUri: result.fbBusinessPictureUri,
      },
    }))
    if (field === 'ad_account' || field === 'page') {
      setOwnerInfoByField(prev => ({ ...prev, [field]: result.ownerBusiness }))
    }
    markPending(field)

    // A successful per-field assign (Change, Reconnect, or the first-time creator-flow assign) is real
    // proof of current live access — assignSingleFacebookAsset throws on grant failure, so getting here
    // means it didn't. Supersede any stale check-fb-access flag for this field until the next
    // Save-triggered check re-evaluates for real.
    setLocallyConfirmedFields(prev => ({ ...prev, [field]: true }))

    switch (field) {
      case 'ad_account':
        setAccountID(assetId)
        Basic.update('fb_account_id', assetId)
        Basic.update('fb_account_name', assetName)
        Basic.update('adaccount_system_user_id', result.systemUserId)
        recheckAdAccountDependentFields(assetId)
        break
      case 'page':
        setPageID(assetId)
        Basic.update('fb_page_id', assetId)
        Basic.update('fb_page_name', assetName)
        Basic.update('page_system_user_id', result.systemUserId)
        break
      case 'pixel':
        setDatasetID(assetId)
        Basic.update('fb_dataset_id', assetId)
        Basic.update('fb_dataset_name', assetName)
        // DRI-214: a dataset connected to an ad account doesn't go through the system-user grant model
        // at all anymore — result.systemUserId is always absent here now, same as Instagram below, so
        // this stays null rather than passing through undefined (which the backend's Zod schema rejects
        // as a missing required field rather than an explicit null).
        Basic.update('dataset_system_user_id', null)
        // This field's own assign already ran its own connectivity check (reflected in result.warning
        // via the field's normal health chip) — clear any stale warning from an earlier ad-account-only
        // recheck so the two don't show conflicting messages.
        setAdAccountDependentWarnings(prev => ({ ...prev, pixel: undefined }))
        break
      case 'offline':
        setOfflineID(assetId)
        Basic.update('offline_event_set_id', assetId)
        Basic.update('fb_offline_name', assetName)
        // DRI-214: same as pixel above — offline datasets don't get a system-user grant either.
        Basic.update('offline_system_user_id', null)
        setAdAccountDependentWarnings(prev => ({ ...prev, offline: undefined }))
        // Offline Dataset is optional and always shown (no separate enable toggle) — presence of a
        // value here is what use_offline_id (sent to the backend) actually tracks.
        Basic.update('use_offline_id', true)
        break
      case 'ig':
        // Instagram has no system-user-based permission model in this codebase (verifyFacebook never
        // granted one either) — result.systemUserId is always absent here, so this stays null.
        setInstagramID(assetId)
        Basic.update('instagram_id', assetId)
        Basic.update('instagram_name', assetName)
        Basic.update('instagram_system_user_id', null)
        // the assign confirmed it's on the current ad account, so drop any not-connected warning a
        // re-check set for the previous pick
        Basic.update('instagram_connected', true)
        setAdAccountDependentWarnings(prev => ({ ...prev, ig: undefined }))
        break
    }

    // A system user now holds this field. The business goes back to system users only once both fields are on
    // the same one (or there's no Page): it's on one identity for both, never a system user on one and a person
    // on the other (DRI-253), and never back to a system-user split. Under a person the old system users stay
    // saved as the target, so the other field may still hold a different one. Until then the person keeps
    // connecting both. Read from the store, not this render's values: the Fix dialog assigns both fields back
    // to back from one render's callbacks.
    const latest = (Store.getState() as Pick<Redux, 'pages'>).pages.business.main
    const stillOnPerson = !!latest.fb_ad_authorizer_id || !!latest.fb_page_authorizer_id
    if ((field === 'ad_account' || field === 'page') && stillOnPerson) {
      const oneSystemUserForBoth =
        !!latest.adaccount_system_user_id &&
        (!latest.fb_page_id || latest.page_system_user_id === latest.adaccount_system_user_id)
      if (oneSystemUserForBoth) {
        Basic.update('fb_ad_authorizer_id', null)
        Basic.update('fb_page_authorizer_id', null)
        setPersonInfo(undefined)
        if (!quiet) {
          toast.success(
            `Moved off ${connectedBy?.name}'s personal login onto a system user. Click Save to keep this change.`
          )
        }
      } else if (
        !quiet &&
        // only when the other field is on a different system user, the split one "Use for both" resolves. With
        // no system user on it (a person connected just now, say), staying on the person is expected: no toast.
        !!(field === 'ad_account' ? latest.page_system_user_id : latest.adaccount_system_user_id)
      ) {
        toast.warn(
          `Still connected by ${connectedBy?.name}'s personal login: the ${
            field === 'ad_account' ? 'Page' : 'Ad Account'
          } isn't on the same system user yet. Use a system user, then Use for both, to move both together.`
        )
      }
    }
  }

  // Page, Offline Dataset, and Instagram are the only fields that can be unassigned outright rather than
  // only swapped — Ad Account/Dataset are required for the business to be considered fully configured
  // (see validateBusiness's FB-Builder branch). Clearing is purely local: there's no FB-side grant to
  // revoke, and the change only takes effect once the panel's own Save runs, same as every other field.
  const handleCleared = (field: 'page' | 'offline' | 'ig') => {
    markPending(field)
    setBmInfoByField(prev => ({ ...prev, [field]: undefined }))
    if (field === 'page') setOwnerInfoByField(prev => ({ ...prev, page: undefined }))

    if (field === 'page') {
      setPageID(undefined)
      Basic.update('fb_page_id', null)
      Basic.update('fb_page_name', null)
      Basic.update('page_system_user_id', null)
    } else if (field === 'offline') {
      setOfflineID(undefined)
      Basic.update('offline_event_set_id', null)
      Basic.update('fb_offline_name', null)
      Basic.update('offline_system_user_id', null)
      setAdAccountDependentWarnings(prev => ({ ...prev, offline: undefined }))
      Basic.update('use_offline_id', false)
    } else {
      setInstagramID(undefined)
      Basic.update('instagram_id', null)
      Basic.update('instagram_name', null)
      Basic.update('instagram_system_user_id', null)
      Basic.update('instagram_connected', undefined)
      setAdAccountDependentWarnings(prev => ({ ...prev, ig: undefined }))
    }
  }

  // Once a refresh finishes, drops this user's server-cached Ad Account/Page lists (built before the new
  // rows existed) and has both fields refetch, so the freshly synced assets show without a reload.
  const reloadAssetLists = async (status: FBAssetsRefreshStatus) => {
    try {
      await businessManagerService.clearFacebookAccessCache()
      setRefreshToken(prev => prev + 1)
      await new Promise<void>(resolve => {
        pendingAssetFetches.current = { remaining: 2, resolve }
      })
      if (status.failedBusinessManagers.length) {
        toast.warning(
          `Refreshed available Facebook assets, but some Business Managers could not be synced: ${status.failedBusinessManagers
            .map(bm => bm.name)
            .join(', ')}. Their lists may be out of date.`
        )
      } else {
        toast.success('Refreshed available Facebook assets.')
      }
    } catch (e) {
      console.error(e)
      toast.error('Refresh finished, but reloading the lists failed. Please reload the page.')
    }
  }

  useEffect(() => {
    if (!fbConnected) return
    businessManagerService
      .getFBAssetsRefreshStatus()
      .then(setRefreshStatus)
      .catch(e => console.error(e))
  }, [fbConnected])

  useEffect(() => {
    if (!refreshingAssets) return
    const interval = setInterval(() => {
      businessManagerService
        .getFBAssetsRefreshStatus()
        .then(setRefreshStatus)
        .catch(e => console.error(e))
    }, REFRESH_POLL_MS)
    return () => clearInterval(interval)
  }, [refreshingAssets])

  useEffect(() => {
    if (wasRefreshing.current && !refreshingAssets && refreshStatus) {
      reloadAssetLists(refreshStatus)
    }
    wasRefreshing.current = refreshingAssets
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshingAssets])

  // Both the page-level button and the fields' empty-results prompt land here: confirm first, since a
  // refresh is expensive and only meant for a genuinely missing asset.
  const handleRefreshFacebookAccess = () => setRefreshDialogOpen(true)

  const confirmRefresh = async () => {
    setStartingRefresh(true)
    try {
      const status = await businessManagerService.refreshFBAssets()
      setRefreshDialogOpen(false)
      if (status.status === 'idle' && status.refreshAvailableAt) {
        // everything was synced within the cooldown, so the server had nothing to re-sync
        toast.info(
          `Your Business Managers were refreshed in the last 15 minutes. You can refresh again after ${new Date(
            status.refreshAvailableAt
          ).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.`
        )
      } else if (status.status === 'idle') {
        // nothing to sync (no Business Managers to refresh), so just reload what's there
        await reloadAssetLists(status)
      } else {
        setRefreshStatus(status)
        toast.info('Refreshing available Facebook assets. The lists will update when it finishes.')
      }
    } catch (e: any) {
      console.error(e)
      // a 400's reason is in the response body; e.message is only axios's status line
      toast.error(
        e?.response?.data?.message ??
          'Failed to start refreshing available Facebook assets. Please try again.'
      )
    } finally {
      setStartingRefresh(false)
    }
  }

  const refreshProgress =
    refreshStatus && refreshStatus.total > 0
      ? Math.round((refreshStatus.completed / refreshStatus.total) * 100)
      : 0

  return (
    <>
      <div className="d-flex justify-content-between mb-4">
        <h4 className="px-4">
          <i className="fab fa-facebook"></i>&nbsp;Facebook Builder
        </h4>
        <div className="d-flex align-items-center">
          <Button
            onClick={handleRefreshFacebookAccess}
            className="mr-2"
            variant="contained"
            color="secondary"
            disabled={refreshingAssets}
          >
            {refreshingAssets ? 'Refreshing...' : 'Refresh Available Assets'}
          </Button>
          <Popover>
            The Ad Account and Page lists include everything in each Facebook Business Manager you
            are an admin on, plus anything assigned to you personally. Marketing Milk syncs them
            from Facebook weekly.
            <br />
            <br />
            If an Ad Account or Page was added in Facebook since then and you need it now, this
            re-syncs your Business Managers. It does not re-authenticate your Facebook login.
            <br />
            <br />
            <strong>
              Warning: A refresh takes several minutes and uses Facebook API capacity shared across
              Marketing Milk. Only use it when an asset is missing, otherwise reach out to Marketing
              Milk support.
            </strong>
          </Popover>
        </div>
      </div>
      {refreshingAssets && refreshStatus && (
        <div className="px-4 mb-4">
          <div
            className="d-flex justify-content-between mb-1"
            style={{ fontSize: 13, color: '#666' }}
          >
            <span>
              {refreshStatus.status === 'queued'
                ? 'Refresh of your Business Managers is queued...'
                : 'Refreshing available assets from your Business Managers...'}{' '}
              You can keep working. The lists update when it finishes.
            </span>
            {refreshStatus.total > 0 && (
              <span>
                {refreshStatus.completed} of {refreshStatus.total}
              </span>
            )}
          </div>
          <LinearProgress
            variant={refreshStatus.total > 0 ? 'determinate' : 'indeterminate'}
            value={refreshProgress}
          />
        </div>
      )}
      <RefreshAssetsDialog
        open={refreshDialogOpen}
        starting={startingRefresh}
        onCancel={() => setRefreshDialogOpen(false)}
        onConfirm={confirmRefresh}
      />
      <Alert
        style={{
          width: 'fit-content',
          marginLeft: '1.5rem',
          marginTop: '1rem',
          marginBottom: '1.5rem',
          fontSize: '0.95rem',
        }}
        severity="info"
      >
        Marketing Milk manages Facebook access through a system user — an automated identity
        invisible in your Facebook Business Manager's user list — that holds the actual asset
        assignment on our behalf. The Ad Accounts, Pages, and Datasets available for you to assign
        below are limited to what <strong>your own Facebook login</strong> currently is assigned to.
        Only Facebook-signed-in users can reassign these connections.
      </Alert>
      {hasPendingChanges && (
        <Alert
          style={{ marginLeft: '1.5rem', marginRight: '1.5rem', marginBottom: '1.5rem' }}
          severity="warning"
        >
          <strong>Changes pending.</strong> The updated assets below are verified with Facebook, but
          won't be saved to this business until you click <strong>Save</strong>.
        </Alert>
      )}
      {!fbConnected && (
        <Alert
          style={{
            width: 'fit-content',
            marginLeft: '1.5rem',
            marginTop: '1rem',
            marginBottom: '1.5rem',
            fontSize: '1rem',
          }}
          severity="error"
        >
          You cannot grant access to Facebook assets if you aren't logged in using Facebook.
        </Alert>
      )}
      <div className="row px-3 pb-3">
        <div className="col-md-6 col-sm-12 col-xs-12">
          <FbAssignableField
            type="ad_account"
            updated={pendingFields.includes('ad_account')}
            title="Facebook Ad Account"
            disabled={!fbConnected}
            accountID={accountID}
            currentAssetId={accountID}
            currentAssetName={main.fb_account_name}
            isNewBusiness={props.new}
            businessID={businessID}
            ownerBusiness={ownerInfoByField.ad_account}
            connectedThrough={bmInfoByField.ad_account}
            hasError={fieldHasError('ad_account')}
            onAssigned={(result, assetId, assetName) =>
              handleAssigned('ad_account', result, assetId, assetName)
            }
            preferSystemUserId={pageSystemUserId}
            currentSystemUserId={adSystemUserId}
            otherAssetId={pageID}
            systemUserWarning={adSystemUserWarning}
            onFixSystemUser={() => setAlignDialogOpen(true)}
            onChangeSystemUser={pageID ? () => setAlignDialogOpen(true) : undefined}
            connectedBy={connectedBy}
            onUsePersonInstead={(id, name) => {
              if (!pageID) return connectAdAccountWithPerson(id, name)
              setAlignCandidate({ field: 'ad_account', id, name })
              setAlignDialogOpen(true)
            }}
            tooltipText="Account to manage paid advertising campaigns and billing on Facebook and Instagram"
            onRefreshAssets={handleRefreshFacebookAccess}
            refreshingAssets={refreshingAssets}
            refreshToken={refreshToken}
            onRefreshComplete={handleAssetsRefreshed}
          />
        </div>
        <div className="col-md-6 col-sm-12 col-xs-12">
          <FbAssignableField
            type="page"
            updated={pendingFields.includes('page')}
            title="Facebook Page"
            optional
            disabled={!fbConnected}
            currentAssetId={pageID}
            currentAssetName={main.fb_page_name}
            isNewBusiness={props.new}
            businessID={businessID}
            ownerBusiness={ownerInfoByField.page}
            connectedThrough={bmInfoByField.page}
            hasError={fieldHasError('page')}
            externalWarning={
              !locallyConfirmedFields.page && errors.includes(BusinessError.lead_access_error)
                ? pageLeadWarningMessage
                : undefined
            }
            onAssigned={(result, assetId, assetName) =>
              handleAssigned('page', result, assetId, assetName)
            }
            preferSystemUserId={adSystemUserId}
            currentSystemUserId={pageSystemUserId}
            otherAssetId={accountID}
            systemUserWarning={pageSystemUserWarning}
            onFixSystemUser={() => setAlignDialogOpen(true)}
            onChangeSystemUser={accountID ? () => setAlignDialogOpen(true) : undefined}
            connectedBy={connectedBy}
            onUsePersonInstead={
              accountID
                ? (id, name) => {
                    setAlignCandidate({ field: 'page', id, name })
                    setAlignDialogOpen(true)
                  }
                : undefined
            }
            clearable
            onCleared={() => handleCleared('page')}
            tooltipText="Public-facing profile for businesses, brands, or organizations on Facebook"
            onRefreshAssets={handleRefreshFacebookAccess}
            refreshingAssets={refreshingAssets}
            refreshToken={refreshToken}
            onRefreshComplete={handleAssetsRefreshed}
          />
        </div>
      </div>
      {alignDialogOpen && accountID && pageID && (
        <SystemUserAlignDialog
          open
          adAccount={
            alignCandidate?.field === 'ad_account'
              ? { id: alignCandidate.id, name: alignCandidate.name }
              : {
                  id: accountID,
                  name: main.fb_account_name,
                  systemUserId: adSystemUserId,
                  businessId: bmInfoByField.ad_account?.id,
                  businessName: bmInfoByField.ad_account?.name,
                  businessPictureUri: bmInfoByField.ad_account?.pictureUri,
                }
          }
          page={
            alignCandidate?.field === 'page'
              ? { id: alignCandidate.id, name: alignCandidate.name }
              : {
                  id: pageID,
                  name: main.fb_page_name,
                  systemUserId: pageSystemUserId,
                  businessId: bmInfoByField.page?.id,
                  businessName: bmInfoByField.page?.name,
                  businessPictureUri: bmInfoByField.page?.pictureUri,
                }
          }
          connectedBy={alignCandidate ? undefined : connectedBy}
          onUsePerson={() => connectWithPerson()}
          onAssigned={(field, result) =>
            alignCandidate?.field === field
              ? handleAssigned(field, result, alignCandidate.id, alignCandidate.name, true)
              : handleAssigned(
                  field,
                  result,
                  field === 'ad_account' ? accountID : pageID,
                  (field === 'ad_account' ? main.fb_account_name : main.fb_page_name) ??
                    result.assetName,
                  true
                )
          }
          onClose={() => {
            setAlignDialogOpen(false)
            setAlignCandidate(undefined)
          }}
        />
      )}
      <div className="row px-3 pb-3">
        <div className="col-md-6 col-sm-12 col-xs-12">
          <FbAssignableField
            type="pixel"
            updated={pendingFields.includes('pixel')}
            title="Dataset"
            disabled={!fbConnected}
            accountID={accountID}
            currentAssetId={datasetID}
            currentAssetName={main.fb_dataset_name}
            isNewBusiness={props.new}
            businessID={businessID}
            ownerBusiness={bmInfoByField.pixel}
            externalWarning={adAccountDependentWarnings.pixel}
            hasError={fieldHasError('pixel')}
            onAssigned={(result, assetId, assetName) =>
              handleAssigned('pixel', result, assetId, assetName)
            }
            tooltipText="A collection of data generated from Facebook campaigns, pages, or offline-event uploads."
          />
        </div>
        <div className="col-md-6 col-sm-12 col-xs-12">
          <FbAssignableField
            type="offline"
            updated={pendingFields.includes('offline')}
            title="Offline Dataset"
            optional
            disabled={!fbConnected}
            accountID={accountID}
            currentAssetId={offlineID}
            currentAssetName={main.fb_offline_name}
            isNewBusiness={props.new}
            businessID={businessID}
            ownerBusiness={bmInfoByField.offline}
            externalWarning={adAccountDependentWarnings.offline}
            hasError={fieldHasError('offline')}
            onAssigned={(result, assetId, assetName) =>
              handleAssigned('offline', result, assetId, assetName)
            }
            clearable
            onCleared={() => handleCleared('offline')}
            tooltipText="Only needed when online and offline data must be managed separately — most businesses can leave this unset and rely on the Dataset above for both."
          />
        </div>
      </div>
      <hr />
      <div className="row px-3 pb-3">
        <div className="col-md-6 col-sm-12 col-xs-12">
          <FbAssignableField
            type="ig"
            updated={pendingFields.includes('ig')}
            title="Instagram Account"
            optional
            disabled={!fbConnected}
            accountID={accountID}
            currentAssetId={instagramID}
            currentAssetName={main.instagram_name}
            isNewBusiness={props.new}
            businessID={businessID}
            initialOwnPictureUri={main.instagram_picture_uri}
            ownerBusiness={bmInfoByField.ig}
            externalWarning={
              adAccountDependentWarnings.ig ??
              (main.instagram_connected === false
                ? 'This Instagram account is not connected to the currently selected Ad Account.'
                : undefined)
            }
            hasError={false}
            onAssigned={(result, assetId, assetName) =>
              handleAssigned('ig', result, assetId, assetName)
            }
            clearable
            onCleared={() => handleCleared('ig')}
          />
        </div>
      </div>
    </>
  )
}

export default Connect(Facebook)
